import React, { useState } from 'react';
import { Form, Input, Button, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined, LeftCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../context/LoadingContext';
import { apiAddTDMProvider } from '../../api';
import { toastError, toastSuccess } from '../../utils/toast';
import {TDM_SOURCE} from "../../assets/constants/default_option";

const INPUT_STYLE = {
  width: '90%',
};

const ICON_STYLE = {
  marginLeft: 8,
  fontSize: '16px',
  color: '#999'
};

interface CreatePromotionProviderRequest {
  name: string;
  apiUrl: string;
  partnerId?: string;
  secret?: string;
  transactionPrefix?: string;
  cpCodeWallet?: string;
  source?: string;
  status?: string;
  proxyAddress?: string[];
  otpConfirm: string;
}

const statusOptions = [
  { label: 'Hoạt động', value: 'active' },
  { label: 'Không hoạt động', value: 'inactive' },
];

const rules = {
  name: [
    { required: true, message: 'Vui lòng nhập tên nhà cung cấp' }
  ],
  apiUrl: [
    { required: true, message: 'Vui lòng nhập API URL' },
    { type: 'url' as const, message: 'Vui lòng nhập URL hợp lệ' }
  ],
  otpConfirm: [
    { required: true, message: 'Vui lòng nhập mã OTP' },
    { min: 6, message: 'Mã OTP phải có ít nhất 6 ký tự' },
  ],
};

export default function CreateTDMProvider() {
  const [form] = Form.useForm<CreatePromotionProviderRequest>();
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: CreatePromotionProviderRequest) => {
    try {
      setSubmitting(true);
      setIsLoading(true);

      // Clean up arrays
      const cleanedValues = {
        ...values,
        proxyAddress: cleanArray(values.proxyAddress || []),
        source: TDM_SOURCE
      };

      const resp = await apiAddTDMProvider(cleanedValues);
      if (resp.status === 200) {
        toastSuccess('Thêm dữ liệu thành công!');
        form.resetFields();
      }
    } catch (error: any) {
      console.error('Error creating TDM provider:', error);
      toastError('Vui lòng kiểm tra lại thông tin!');
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  const cleanArray = (arr: string[]) => {
    return Array.from(new Set(arr.filter(item => item && item.trim() !== '')));
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20 }}>
        <Button
          onClick={() => navigate(-1)}
          icon={<LeftCircleOutlined />}
          style={{ background: 'gray', color: 'white' }}
        >
          Trở lại
        </Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Form
          style={{ width: '700px' }}
          form={form}
          onFinish={handleSubmit}
          labelAlign="left"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={{
            status: 'active',
          }}
        >
          <Form.Item
            label="Tên nhà cung cấp"
            name="name"
            rules={rules.name}
          >
            <Input placeholder="Nhập tên nhà cung cấp" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="API URL"
            name="apiUrl"
            rules={rules.apiUrl}
          >
            <Input placeholder="https://example.com/api" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Partner ID"
            name="partnerId"
          >
            <Input placeholder="Nhập partner ID" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Secret"
            name="secret"
          >
            <Input.Password placeholder="Nhập secret" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Transaction Prefix"
            name="transactionPrefix"
          >
            <Input placeholder="Nhập transaction prefix" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="CP Code Wallet"
            name="cpCodeWallet"
          >
            <Input placeholder="Nhập CP code wallet" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Trạng thái"
            name="status"
          >
            <Select
              placeholder="Chọn trạng thái"
              options={statusOptions}
              style={INPUT_STYLE}
            />
          </Form.Item>

          {/* Proxy Address List */}
          <Form.List name="proxyAddress" initialValue={['']}>
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {fields.map((field, index) => (
                  <Form.Item
                    label={index === 0 ? "Proxy Address" : " "}
                    colon={index === 0}
                    key={field.key}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        noStyle
                      >
                        <Input
                          placeholder="Nhập địa chỉ proxy"
                          style={INPUT_STYLE}
                        />
                      </Form.Item>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                            style={ICON_STYLE}
                          />
                        )}
                        {index === fields.length - 1 && (
                          <PlusOutlined
                            onClick={() => add()}
                            style={ICON_STYLE}
                          />
                        )}
                      </div>
                    </div>
                  </Form.Item>
                ))}
              </div>
            )}
          </Form.List>

          <Form.Item
            label="Mã OTP xác nhận"
            name="otpConfirm"
            rules={rules.otpConfirm}
          >
            <Input placeholder="Nhập OTP xác nhận" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Thêm
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}

